import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Bell, LayoutDashboard, Shield, LogOut, User } from "lucide-react";
import logo from "@/assets/fl-cars-logo.png";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const LINKS = [
  { to: "/inventory", label: "Inventory" },
  { to: "/financing", label: "Financing" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
    <img src={logo} alt="F&L Cars" className="h-16 w-auto transition-transform group-hover:scale-105" />
  </Link>
);

export const Navbar = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, [user]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong border-b border-slate-200" : "bg-transparent"
      }`}
    >
      <nav className="container-x flex h-20 items-center justify-between">
        <Logo />
        <div className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-gold" : "text-slate-700 hover:text-slate-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <button
                data-testid="nav-notifications-btn"
                onClick={() => navigate("/dashboard?tab=notifications")}
                className="relative hidden rounded-full p-2 text-slate-700 hover:text-slate-900 sm:block"
              >
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-[#EEF3FC]">
                    {unread}
                  </span>
                )}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 py-1 pl-1 pr-3 transition-colors hover:border-gold/40">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/20 text-gold"><User className="h-4 w-4" /></div>
                    )}
                    <span className="hidden max-w-[100px] truncate text-sm text-slate-900 md:block">{user.name || "Account"}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 border-slate-200 bg-[#FFFFFF] text-slate-700">
                  <div className="px-2 py-1.5 text-xs text-slate-600">{user.email}</div>
                  <DropdownMenuSeparator className="bg-slate-200" />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-slate-200">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> My Dashboard
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-slate-200">
                      <Shield className="mr-2 h-4 w-4 text-gold" /> Admin Portal
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-slate-200" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer text-red-400 focus:bg-slate-200 focus:text-red-400">
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button data-testid="nav-signin-btn" onClick={() => navigate("/login")} className="btn-gold hidden rounded-full px-6 sm:flex">
              Sign In
            </Button>
          )}
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" className="rounded-lg p-2 text-slate-900 lg:hidden">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 border-slate-200 bg-[#EEF3FC] text-slate-900">
              <div className="mt-8 flex flex-col gap-2">
                {LINKS.map((l) => (
                  <NavLink key={l.to} to={l.to} className="rounded-lg px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-100">
                    {l.label}
                  </NavLink>
                ))}
                {!user && (
                  <Button onClick={() => navigate("/login")} className="btn-gold mt-4 rounded-full">Sign In</Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
